export type LifeCycleEntity = {
	load?: () => Promise<void>;
	awake?: () => Promise<void>;
	start?: () => Promise<void>;
	stop?: () => Promise<void>;
	sleep?: () => Promise<void>;
	unload?: () => Promise<void>;
};

type LifeCycleFunctions = keyof LifeCycleEntity;

enum Steps {
	Destroyed = -1,
	Loaded = 0,
	Awake = 1,
	Started = 2
}

export class LifeCycle implements LifeCycleEntity {
	private entities: LifeCycleEntity[] = [];

	private steps: { id: Steps; execute: LifeCycleFunctions; unExecute: LifeCycleFunctions }[] = [
		{ id: Steps.Loaded, execute: 'load', unExecute: 'unload' },
		{ id: Steps.Awake, execute: 'awake', unExecute: 'sleep' },
		{ id: Steps.Started, execute: 'start', unExecute: 'stop' }
	];

	public isLoaded() {
		return this.currentStep >= Steps.Loaded;
	}
	public isAwake() {
		return this.currentStep >= Steps.Awake;
	}
	public isStarted() {
		return this.currentStep >= Steps.Started;
	}

	public targetStep: Steps = Steps.Destroyed;
	public currentStep: Steps = Steps.Destroyed;

	public register = async (entity: LifeCycleEntity): Promise<void> => {
		this.entities.push(entity);

		if (this.isLoaded()) await entity.load?.();
		if (this.isAwake()) await entity.awake?.();
		if (this.isStarted()) await entity.start?.();
	};

	private async updateStep() {
		const direction = Math.sign(this.targetStep - this.currentStep);
		if (direction === 0) return;
		if (direction > 0) {
			this.currentStep += direction;
			await this.envokeAll(this.steps[this.currentStep].execute);
		} else if (direction < 0) {
			await this.envokeAll(this.steps[this.currentStep].unExecute);
			this.currentStep = this.currentStep + direction;
		}
		await this.updateStep();
	}

	private envokeAll = async (func: LifeCycleFunctions): Promise<(void | undefined)[]> => {
		return Promise.all(
			this.entities.map((entity) => {
				try {
					return entity[func]?.();
				} catch (e) {
					console.error(e);
				}
			})
		);
	};

	public async load() {
		this.targetStep = Steps.Loaded;
		return this.updateStep();
	}
	public async awake() {
		this.targetStep = Steps.Awake;
		return this.updateStep();
	}
	public async start() {
		this.targetStep = Steps.Started;
		return this.updateStep();
	}
	public async stop() {
		this.targetStep = Steps.Awake;
		return this.updateStep();
	}
	public async sleep() {
		this.targetStep = Steps.Loaded;
		return this.updateStep();
	}
	public async unload() {
		this.targetStep = Steps.Destroyed;
		return this.updateStep();
	}
}
